/**
 * Kingdom Factor Collective - Category Page
 * - Shows articles filtered by category
 * - Category description header
 * - Same layout as Index but with category context
 */

import { useState, useCallback, useRef, useEffect } from "react"
import { Link, router } from "@inertiajs/react"
import { Search, X } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { US_STATES } from "@/lib/us-states"
import { COUNTRIES } from "@/lib/countries"
import {
  ArticleCard,
  Breadcrumbs,
  Pagination,
} from "./components"

export default function Category({
  seo,
  category = {},
  articles = [],
  pagination = {},
  availableStates = [],
  availableCountries = [],
  activeState = null,
  activeCountry = null,
  activeQuery = null,
  settings = {},
}) {
  const [searchValue, setSearchValue] = useState(activeQuery || "")
  const { scheduleCallUrl = "#" } = settings
  const debounceRef = useRef(null)

  // Location filter groups: US states first (full names, A–Z), then other
  // countries below (full names, alphabetical). Built from the codes present in
  // published articles; US_STATES/COUNTRIES supply the labels (read-only) and
  // the US_STATES lookup drops any non-state values from the states group.
  const stateNameByCode = Object.fromEntries(US_STATES.map((s) => [s.value, s.label]))
  const countryNameByCode = Object.fromEntries(COUNTRIES.map((c) => [c.code, c.name]))
  const stateOptions = availableStates
    .filter((code) => stateNameByCode[code])
    .map((code) => ({ value: code, label: stateNameByCode[code] }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const countryOptions = availableCountries
    .filter((code) => code !== "US")
    .map((code) => ({ value: code, label: countryNameByCode[code] || code }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const hasLocationFilter = stateOptions.length > 0 || countryOptions.length > 0
  const locationValue = activeCountry
    ? `country:${activeCountry}`
    : activeState
      ? `state:${activeState}`
      : "all"

  // Sync search value when activeQuery prop changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchValue(activeQuery || "")
  }, [activeQuery])

  const performSearch = useCallback((query) => {
    const params = {}
    if (activeState) params.state = activeState
    if (activeCountry) params.country = activeCountry
    if (query) params.q = query
    // Use preserveState: false when clearing search to ensure a full page refresh
    const options = { preserveScroll: true, preserveState: !!query }
    router.get(`/collective/${category.slug}`, params, options)
  }, [category.slug, activeState, activeCountry])

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchValue(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      performSearch(value)
    }, 300)
  }, [performSearch])

  const clearSearch = useCallback(() => {
    setSearchValue("")
    if (debounceRef.current) clearTimeout(debounceRef.current)
    performSearch("")
  }, [performSearch])

  // Location filter handler. Values are namespaced ("state:IN" / "country:CA")
  // so overlapping US-state and country codes never collide; decode to the
  // matching query param. Preserves the current search query.
  const handleLocationChange = useCallback((value) => {
    const params = {}
    if (value.startsWith("state:")) params.state = value.slice("state:".length)
    else if (value.startsWith("country:")) params.country = value.slice("country:".length)
    if (activeQuery) params.q = activeQuery
    router.get(`/collective/${category.slug}`, params, { preserveState: true, preserveScroll: true })
  }, [category.slug, activeQuery])

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="collective" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section */}
        <section className="bg-zinc-50 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Breadcrumbs */}
            <div className="mb-8">
              <Breadcrumbs
                items={[
                  { label: "Collective", href: "/collective" },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="text-center">
              <p className="text-kf-red font-semibold uppercase tracking-wider mb-4">
                Category
              </p>
              <h1 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                {category.name}
              </h1>
              {category.description && (
                <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
                  {category.description}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="bg-zinc-50 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Search Bar */}
            <div className="mb-8 max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="text"
                  placeholder="Search by title, author, interviewee, city, or state..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10 bg-white border-zinc-200"
                />
                {searchValue && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Article Count & State Filter */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-zinc-500 text-sm">
                {pagination.totalCount || articles.length} article{(pagination.totalCount || articles.length) !== 1 ? "s" : ""}
              </p>
              {hasLocationFilter && (
                <Select
                  value={locationValue}
                  onValueChange={handleLocationChange}
                >
                  <SelectTrigger className="w-[200px] bg-white border-zinc-200">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {stateOptions.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>U.S. States</SelectLabel>
                        {stateOptions.map((o) => (
                          <SelectItem key={`state:${o.value}`} value={`state:${o.value}`}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {countryOptions.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Other Locations</SelectLabel>
                        {countryOptions.map((o) => (
                          <SelectItem key={`country:${o.value}`} value={`country:${o.value}`}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Articles Grid */}
            {articles.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                {activeQuery ? (
                  <>
                    <p className="text-zinc-500 mb-2">
                      No articles found matching "{activeQuery}" in {category.name}.
                    </p>
                    <button
                      onClick={clearSearch}
                      className="text-kf-red font-semibold hover:underline"
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-zinc-500">No articles found in this category.</p>
                    <Link
                      href="/collective"
                      className="inline-block mt-4 text-kf-red font-semibold hover:underline"
                    >
                      View all articles
                    </Link>
                  </>
                )}
              </div>
            )}

            {/* Pagination */}
            <Pagination
              pagination={pagination}
              baseUrl={`/collective/${category.slug}`}
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-kf-gradient py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
              WANT TO SHARE YOUR STORY?
            </h2>
            <p className="text-white/80 mb-8">
              Join our community of faith-driven leaders and share how God is working in your business.
            </p>
            <Button variant="kf-secondary" asChild>
              <Link href="/collective">Explore The KF Collective</Link>
            </Button>
          </div>
        </section>

        <Footer activePage="collective" />
      </div>
    </>
  )
}
